import { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useDeleteMyCharacterMutation,
  useEditCharacterDetailMutation,
} from '~/features/character';
import { useModal } from '~/hooks';
import { CommonInput } from '~/hooks/CreateNewCharacter/useDetailInput';
import useDirectionInput from '~/hooks/SetProfile/useDirectionInput';
import {
  DeleteMyCharacterRequest,
  ModalParams,
  GetCharacterDetailsData,
  EditCharacterDetailRequest,
} from '~/types';
import { PAGE_ROUTE } from '~/constants';
import { MEGABYTE } from '~/constants/size';
import useCreateNewCharacterStore from '~/store/useCreateNewCharacterStore';
import useToast from '~/store/useToast';

const PHOTO_CAPACITY = 10;

interface UseEditCharacterProps {
  characterId: string;
  data?: GetCharacterDetailsData;
}

const useEditCharacter = ({ characterId, data }: UseEditCharacterProps) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { mutate: editMutate, isPending: editPending } =
    useEditCharacterDetailMutation();
  const { mutate: deleteMutate, isPending: deletePending } =
    useDeleteMyCharacterMutation();
  const { openModal } = useModal();

  const [profileFile, setProfileFile] = useState<File | undefined>();
  const [profileImage, setProfileImage] = useState<string | undefined>(
    data?.profile_image || undefined,
  );

  const [commonInput, setCommonInput] = useState<CommonInput>({
    characterName: data?.character_name || '',
    opening: data?.opening || '',
    tagLine: data?.tag_line || '',
    persona: data?.persona || '',
  });
  const [showPersona, setShowPersona] = useState<boolean>(false);

  useEffect(() => {
    setShowPersona(Number(data?.show_persona) === 1 ? true : false);
  }, [data?.show_persona]);

  const { textRef, handleDirectionClick, handleUserNameClick } =
    useDirectionInput({
      setInput: setCommonInput,
    });

  const { characterName, opening, tagLine, persona } = commonInput;

  const uploadType = useCreateNewCharacterStore((state) => state.generateType);

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (file.size >= PHOTO_CAPACITY * MEGABYTE) {
      showToast('error', `cannot upload over ${PHOTO_CAPACITY}MB`);
      return;
    }

    if (
      !extension ||
      (extension !== 'jpeg' && extension !== 'jpg' && extension !== 'png')
    ) {
      showToast('error', 'The file format can only be JPG, JPEG, PNG files.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result) {
        if (typeof reader.result !== 'string') return;
        setProfileFile(file);
        setProfileImage(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteImageClick = () => {
    setProfileFile(undefined);
    setProfileImage(undefined);
  };

  const handleEditClick = () => {
    const requestData: EditCharacterDetailRequest = {
      profile_image: profileFile,
      character_id: characterId,
      character_name: characterName,
      opening,
      tag_line: tagLine,
      persona,
      show_persona: showPersona ? '1' : '0',
    };

    if (requestData) {
      editMutate(requestData, {
        onSuccess({ status }) {
          navigate(PAGE_ROUTE.MYPAGE, { replace: true });
        },
      });
    }
  };

  const handleDeleteModalClick = () => {
    const requestData: DeleteMyCharacterRequest = {
      character_id: characterId,
    };

    const deleteModalData: ModalParams = {
      title: 'Delete character',
      content: 'Are you sure you want to delete a character?',
      type: 'double_red',
      click: () =>
        deleteMutate(requestData, {
          onSuccess({ status }) {
            navigate(PAGE_ROUTE.MYPAGE, { replace: true });
          },
        }),
      buttonName: 'Delete',
    };

    openModal(deleteModalData);
  };

  const GenerateButtonDisabled =
    !profileImage ||
    !commonInput.characterName ||
    !commonInput.opening ||
    !commonInput.tagLine ||
    !commonInput.persona;

  const isPending = editPending || deletePending;

  return {
    profileImage,
    setProfileImage,
    commonInput,
    setCommonInput,
    showPersona,
    setShowPersona,
    textRef,
    handleUserNameClick,
    handleDirectionClick,
    GenerateButtonDisabled,
    uploadType,
    isPending,
    handleEditClick,
    handleDeleteModalClick,
    handleImageChange,
    handleDeleteImageClick,
  };
};

export default useEditCharacter;
